<template>
  <div class="mt-5 mb-5">
    <QuickFilterView
      :quick-filter="quickFilter"
      @quick-filter-changed="quickFilterChanged"
    ></QuickFilterView>
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      :show-export="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="projects"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div>
            <!-- default fields -->
            <div v-if="column.field === 'etablissement.codeEtablissement'">
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="props.row['id'] + '_' + organisme.codeEtablissement"
                >
                  {{ organisme.codeEtablissement }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'etablissement.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'projet.nom'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["nom"] }}
            </div>
            <div
              v-if="column.field === 'statut'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <StatutDossierTag
                v-if="loggedUser"
                :dossier="props.row"
                can-see-instruction-comment
                :logged-user="loggedUser"
              />
            </div>
            <div v-if="column.field === 'etat'">
              {{ props.row["etat"] }}
            </div>
            <div v-if="column.field === 'natureAireEducative'">
              {{ props.row["natureAireEducative"] }}
            </div>
            <div v-if="column.field === 'instructeur'">
              <InstructeurView
                v-if="loggedUser"
                :key="props.row['id']"
                :projet="props.row"
                :logged-user="loggedUser"
                :type-dossier="props.row['typeDossier']"
                @add-instructeur="selectInstructeur"
                @remove-instructeur="removeInstructeur"
              />
            </div>
            <div
              v-if="column.field === 'dateModification'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ formatDateTime(props.row[column.field]) }}
            </div>
            <div v-if="column.field === 'action'">
              <div class="buttons">
                <b-button
                  icon-left="eye"
                  type="is-link is-outlined"
                  :title="$t('consult-projet')"
                  :label="$t('consult-inscription')"
                  @click="consult($event, props.row['projetId'], props.row['id'])"
                >
                </b-button>
              </div>
            </div>

            <!-- hidden fields -->
            <div
              v-if="column.field === 'codeDepartement'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].codeDepartement.replace(/^0+/, "") }}
              </div>
            </div>
            <div
              v-if="column.field === 'ville'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].ville }}
              </div>
            </div>
            <div
              v-if="column.field === 'structure.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['structures']">
                <div
                  v-for="organisme in props.row['structures'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'enseignant.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['enseignants'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>
            <div
              v-if="column.field === 'referent.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['referents'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>

            <!-- non customised fields -->
            <div
              v-if="!column.custom"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div>{{ props.row[column.field] }}</div>
            </div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { EnumHelper } from "@/model/bean/EnumHelper"
import {
  FilterDTO,
  PaginationOrder,
  ProjetInscriptionDTO,
  QuickFilter,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { Filters } from "@/model/bean/Filters"
import RouterUtils from "@/router/RouterUtils"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import StatutDossierTag from "@/views/dossier/StatutDossierTag.vue"
import { defineComponent } from "vue"
import { useTableView } from "@/hooks/useTableView"
import Column from "@/views/tables/Column"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import QuickFilterView from "./QuickFilterView.vue"
import InstructeurView from "@/views/dossier/InstructeurView.vue"
import { ProjectService } from "@/services/rest/project/ProjectService"
import { LoginService } from "@/services/rest/login/LoginService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { SortOrder } from "@/model/SortOrder"
import { BButton, BTable, BTableColumn } from "buefy"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "InscriptionsView",
  components: {
    TableFilterView,
    StatutDossierTag,
    QuickFilterView,
    InstructeurView,
    BButton,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: true,
    },
    activeQuickFilter: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      instructionService: InstructionService.INSTANCE,
      projectService: ProjectService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,

      loggedUser: null as UtilisateurDTO | null,
      projects: [] as Array<ProjetInscriptionDTO>,
      quickFilter: "MY_ASSIGNMENTS" as QuickFilter | undefined,
      // Hook initialized in created()
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView?.state.tableRef
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  watch: {
    activeQuickFilter(): void {
      this.activeQuickFilterChanged()
    },
  },
  created(): void {
    this.loggedUser = this.loginService.getLoggedUser() || null
    if (!this.loggedUser || !this.loggedUser.id) {
      return
    }
    // Initialize hook with proper binding
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "inscriptions-table",
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    this.activeQuickFilterChanged()
  },
  mounted(): void {
    this.tableView?.initMounted()
  },
  methods: {
    // Réexposer les méthodes du tableau
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },
    updateVisibleColumns(columns: Array<Column>): void {
      this.tableView?.updateVisibleColumns(columns)
    },
    updateActiveFilters(filters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(filters)
    },
    activeQuickFilterChanged(): void {
      if (this.activeQuickFilter == 0) {
        this.quickFilter = "MY_ASSIGNMENTS"
      } else if (this.activeQuickFilter == 1) {
        this.quickFilter = "NOT_ASSIGNED"
      } else {
        this.quickFilter = undefined
      }
    },
    async reloadData(): Promise<void> {
      const statutFilter = Filters.asFilter({
        property: "statut",
        values: ["EN_ATTENTE", "INSTRUCTION_EN_COURS"],
      })

      try {
        const paginatedProjectsList = await this.instructionService.getInscriptions(
          this.graeId,
          this.currentPagedSortedParameter,
          [...this.activeFilters, statutFilter],
          this.quickFilter,
        )
        this.projects = paginatedProjectsList.elements
        this.updateTotal(paginatedProjectsList.count)
      } catch (e: any) {
        // Silent catch
      }
    },
    getInitialColumns(): Array<Column> {
      return [
        // default fields
        {
          field: "etablissement.name",
          label: this.$t("graePage.details.projets.etablissements").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.codeEtablissement",
          label: this.$t("graePage.details.projets.uais").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projet.nom",
          label: this.$t("graePage.details.projets.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "statut",
          label: this.$t("graePage.details.projets.statut").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allStatusWithoutArchivedAndPassable,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etat",
          label: this.$t("graePage.details.projets.etat").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "natureAireEducative",
          label: this.$t("graePage.details.projets.nature-ae").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allNatures,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "dateModification",
          label: this.$t("graePage.details.projets.dateModification").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "instructeur",
          label: this.$t("graePage.details.projets.instructeur").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        // hidden fields
        {
          field: "action",
          label: this.$t("projetsListPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.projets.departement").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.projets.ville").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "structure.name",
          label: this.$t("graePage.details.projets.structures").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "enseignant.name",
          label: this.$t("graePage.details.projets.enseignants").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "referent.name",
          label: this.$t("graePage.details.projets.referents").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
      ]
    },
    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "projet.nom"
      defaultSort.desc = false
      return [defaultSort]
    },
    showProjectDetailsPage(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId))
    },
    consult(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      const route = this.$router.resolve(
        RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId),
      )
      window.open(route.href)
    },
    formatDateTime(date: Date): string {
      return DateUtils.formatDateTime(date)
    },
    quickFilterChanged(quickFilter: QuickFilter | undefined): void {
      this.quickFilter = quickFilter
      const params = this.$router.currentRoute.params || {}
      let tabIndex
      if (this.quickFilter === "MY_ASSIGNMENTS") {
        tabIndex = 0
      } else if (this.quickFilter === "NOT_ASSIGNED") {
        tabIndex = 1
      } else {
        tabIndex = 2
      }
      const currentActiveQuickFilter = params["activeQuickFilter"] || "0"
      if (tabIndex.toString() !== currentActiveQuickFilter) {
        params["activeQuickFilter"] = tabIndex.toString()
        this.$router.push({ params: params })
      }
      this.tableView?.reload()
    },
    async selectInstructeur(
      instructeur: UtilisateurDTO,
      projetId: string,
      typeDossier: string,
    ): Promise<void> {
      try {
        if (instructeur) {
          if (typeDossier === "DOSSIER_INSCRIPTION") {
            await this.projectService.updateInstructeur(projetId, instructeur)
          } else {
            console.error("Instruction non définie pour ce type de dossier : " + typeDossier)
          }

          this.infoReportingService.success(
            this.$t("instructionPage.inscriptions.instructeur-changed").toString(),
          )
          this.projects = []
          await this.tableView?.reload()
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("instructionPage.inscriptions.add-instructeur-error").toString(),
          e as Error,
        )
      }
    },
    async removeInstructeur(
      instructeur: UtilisateurDTO,
      projetId: string,
      typeDossier: string,
    ): Promise<void> {
      try {
        if (instructeur) {
          if (typeDossier === "DOSSIER_INSCRIPTION") {
            await this.projectService.removeInstructeur(projetId, instructeur)
          } else {
            console.error("Instruction non définie pour ce type de dossier : " + typeDossier)
          }

          this.infoReportingService.success(
            this.$t("instructionPage.inscriptions.instructeur-removed").toString(),
          )
          this.projects = []
          await this.tableView?.reload()
        }
      } catch (e: any) {
        console.log(e)
        const defaultErrorMessage = this.$t(
          "instructionPage.inscriptions.remove-instructeur-error",
        ).toString()
        this.infoReportingService.error(e.message ? e.message : defaultErrorMessage, e as Error)
      }
    },
  },
})
</script>
